import ArrowLeftTwoIcon from "@assets/icons/arrowLeftTwo"
import EmailIcon from "@assets/icons/email"
import { Button } from "@components/Button"
import formatDateFromISOString from "src/utils/formatDate"
import PhoneInput from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"

const AppointmentDetails = ({
  appointmentDetails,
  onEdit,
  onCancel,
  isCancelLoading = false,
  showEditButton = true,
  isViewOnly = false,
  className = "",
}) => {
  const getAppointmentDate = () => {
    const date = new Date(appointmentDetails.startDateTime)
    return date.toLocaleDateString()
  }

  const getAppointmentTimings = () => {
    const startDate = new Date(appointmentDetails.startDateTime)
    const endDate = new Date(appointmentDetails.endDateTime)
    return {
      startTime:
        startDate.getHours().toString().padStart(2, "0") +
        " : " +
        startDate.getMinutes().toString().padStart(2, "0"),
      endTime:
        endDate.getHours().toString().padStart(2, "0") +
        " : " +
        endDate.getMinutes().toString().padStart(2, "0"),
    }
  }

  // Function to check if appointment is in the past (completed)
  const isAppointmentCompleted = () => {
    const appointmentDate = new Date(appointmentDetails.startDateTime)
    const currentDate = new Date()
    return appointmentDate < currentDate
  }

  // Check if appointment can be cancelled (not completed and not already cancelled)
  const canCancelAppointment =
    !isAppointmentCompleted() && appointmentDetails.status !== "cancelled"

  // Handler for cancel button click that checks if cancellation is allowed
  const handleCancelClick = () => {
    if (canCancelAppointment) {
      onCancel()
    }
  }

  return (
    <div
      className={`rounded-2xl bg-[--pure-white] flex justify-center sm:justify-normal overflow-y-scroll no-scrollbar ${className}`}
    >
      <div className="flex gap-10 grow flex-wrap bg-[--pure-white] rounded-2xl p-4 sm:py-8 sm:px-10 h-max">
        <div className="flex flex-col gap-4 w-full max-w-80">
          <div className="flex gap-4">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="firstName"
                className="text-[#344054] text-sm font-medium"
              >
                First name*
              </label>
              <div className="flex justify-between items-center rounded-lg px-3 py-2 bg-[--quinary-gray-light]">
                <input
                  id="firstName"
                  placeholder="first name"
                  className="w-full bg-transparent"
                  disabled
                  value={appointmentDetails.firstName}
                ></input>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="lastName"
                className="text-[#344054] text-sm font-medium"
              >
                Last name*
              </label>
              <div className="flex justify-between items-center rounded-lg px-3 py-2 bg-[--quinary-gray-light]">
                <input
                  id="lastName"
                  placeholder="last name"
                  className="w-full bg-transparent"
                  disabled
                  value={appointmentDetails.lastName}
                ></input>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="email"
              className="text-[#344054] text-sm font-medium"
            >
              Email*
            </label>
            <div className="flex justify-between gap-2 items-center rounded-lg px-3 py-2 bg-[--quinary-gray-light]">
              <EmailIcon stroke="#BCBEC0" />
              <input
                className="w-full bg-transparent"
                id="email"
                placeholder="email@gmail.com"
                type="email"
                disabled
                value={appointmentDetails.email}
              ></input>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="contact"
              className="text-[#344054] text-sm font-medium"
            >
              Contact*
            </label>
            <PhoneInput
              disabled
              value={appointmentDetails.contact}
              country="in"
              containerStyle={{
                width: "100%",
                border: "1px solid var(--primary-gray-light)",
              }}
              inputStyle={{
                width: "100%",
                border: "none",
                color: "var(--text-light-gray)",
                backgroundColor: "var(--quinary-gray-light)",
              }}
              inputProps={{
                placeholder: "0000000000",
              }}
              buttonStyle={{
                border: "none",
                backgroundColor: "transparent",
              }}
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 min-w-max">
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium text-[#344054]">Date :</div>
            <div className="flex gap-4">
              <div className="h-11 tracking-[0.2rem] flex items-center bg-[--quinary-gray-light] px-4 rounded-lg border border-[--primary-gray-light] font-medium text-base text-[--text-light-gray]">
                {getAppointmentDate()}
              </div>
              <div className="rounded-lg px-4 flex items-center font-medium text-base bg-[--quinary-gray-light] text-[--text-light-gray]">
                {
                  formatDateFromISOString(appointmentDetails.startDateTime)
                    .dayAsName
                }
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium text-[#344054]">
                Start time*
              </div>
              <div className="h-11 tracking-[0.2rem] flex items-center px-4 rounded-lg bg-[--quinary-gray-light] font-medium text-base text-[--text-light-gray]">
                {getAppointmentTimings().startTime}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium text-[--text-light-gray]">
                End time*
              </div>
              <div className="h-11 tracking-[0.17rem] flex items-center px-4 rounded-lg bg-[--quinary-gray-light] font-medium text-base text-[--text-light-gray]">
                {getAppointmentTimings().endTime}
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium text-[#344054]">
                Duration*
              </div>
              <div className="h-11 flex items-center px-4 rounded-lg bg-[--quinary-gray-light] font-medium text-base text-[--text-light-gray]">
                {appointmentDetails.duration} min
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium text-[#344054]">
                Select location*
              </div>
              <div className="h-11 flex items-center px-4 rounded-lg bg-[--quinary-gray-light] font-medium text-base text-[--text-light-gray]">
                {appointmentDetails.location}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium text-[--text-light-gray]">
                select type*
              </div>
              <div className="h-11 flex items-center px-4 rounded-lg bg-[--quinary-gray-light] font-medium text-base text-[--text-light-gray]">
                {appointmentDetails.type}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 grow">
          <div className="flex flex-col gap-2 rounded-2xl bg-[--pure-white] pb-8">
            <div className="text-sm font-medium text-[#344054]">Notes</div>
            <textarea
              disabled
              value={appointmentDetails.note}
              placeholder="Add note for future reference"
              type="text"
              className="min-h-48 w-full max-w-4xl py-2 px-3 rounded-lg bg-[--quinary-gray-light]"
            ></textarea>
            <div className="text-xs">
              This is only for your reference, Patient will not be able to
              access it.
            </div>
          </div>

          <div className="flex gap-2 mt-10 justify-end">
            {showEditButton && (
              <button
                onClick={onEdit}
                className="flex items-center gap-2 border border-[--primary-blue] text-[--primary-blue] p-2 text-sm font-bold rounded-lg"
              >
                <ArrowLeftTwoIcon />
                EDIT
              </button>
            )}
            {!isViewOnly ? (
              <Button
                loading={isCancelLoading}
                onClick={onCancel}
                className="grow max-w-80 rounded-lg p-2 text-[--pure-white] bg-[--primary-blue] font-bold"
              >
                CONFIRM AND SEND INVITE
              </Button>
            ) : (
              <Button
                loading={isCancelLoading}
                onClick={handleCancelClick}
                disabled={!canCancelAppointment}
                className="grow max-w-80 rounded-lg p-2 text-[#B90022] bg-[--pure-white] border font-bold !important"
                style={{
                  color: canCancelAppointment ? "#B90022" : "#ccc",
                  backgroundColor: "var(--pure-white)",
                  border: "1.5px solid #EFF0F0",
                  fontWeight: "700",
                  letterSpacing: "1.25px",
                  cursor: canCancelAppointment ? "pointer" : "not-allowed",
                  opacity: canCancelAppointment ? 1 : 0.6,
                }}
              >
                {appointmentDetails.status === "cancelled"
                  ? "APPOINTMENT CANCELLED"
                  : "CANCEL APPOINTMENT"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppointmentDetails
